import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  ChildCare as MinorIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  Tab as TabIcon,
} from '@mui/icons-material';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import { useAuth } from '../../hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { getVisibleTabKeys } from '../../services/configService';
import {
  getConfigurableTabsForMinor,
  initialMinorEditAllowedTabs,
} from '../../utils/minorTabPolicy';
import jointAccountService, { 
  JointAccountSummary, 
  JointAccountMember 
} from '../../services/jointAccountService';

const MinorAccessManager: React.FC = () => {
  const { user, hasAdvancedMailbox } = useAuth();
  const { data: visibleTabKeys = [] } = useQuery({
    queryKey: ['visibleTabKeys'],
    queryFn: getVisibleTabKeys,
    staleTime: 5 * 60 * 1000,
  });

  const configurableTabs = useMemo(
    () => getConfigurableTabsForMinor(visibleTabKeys, hasAdvancedMailbox),
    [visibleTabKeys, hasAdvancedMailbox],
  );
  const configurableTabKeys = useMemo(
    () => configurableTabs.map((tab) => tab.key),
    [configurableTabs],
  );
  const [members, setMembers] = useState<JointAccountMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [personalAccountId, setPersonalAccountId] = useState<string | null>(null);
  
  const [addMinorDialogOpen, setAddMinorDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<JointAccountMember | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [newMinor, setNewMinor] = useState({
    username: '',
    displayName: '',
    password: '',
    confirmPassword: '',
    dailyLimit: 10,
    weeklyLimit: undefined as number | undefined,
    monthlyLimit: undefined as number | undefined,
    perTransactionLimit: 5,
    requiresApproval: true,
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [editPassword, setEditPassword] = useState('');
  const [editConfirmPassword, setEditConfirmPassword] = useState('');
  const [showEditPassword, setShowEditPassword] = useState(false);
  // Tab permissions for the minor being edited (null = use defaults)
  const [editAllowedTabs, setEditAllowedTabs] = useState<string[]>([]);

  useEffect(() => {
    loadAccountMembers();
  }, []);

  const loadAccountMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      const accounts = await jointAccountService.getMyAccounts();
      const personalAccount = accounts?.find(a => 
        (a.accountType === 'FAMILY' || a.accountType === 'PERSONAL') && 
        (a.userRole === 'PRIMARY' || a.userRole === 'ADMIN')
      );
      
      if (personalAccount) {
        setPersonalAccountId(personalAccount.jointAccountId);
        const membersList = await jointAccountService.getMembers(personalAccount.jointAccountId);
        setMembers(membersList || []);
      } else {
        setMembers([]);
        setPersonalAccountId(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load account members');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMinor = async () => {
    if (!newMinor.username.trim()) {
      setCreateError('Username is required');
      return;
    }
    if (!newMinor.password.trim()) {
      setCreateError('Password is required');
      return;
    }
    if (newMinor.password.length < 8) {
      setCreateError('Password must be at least 8 characters');
      return;
    }
    if (newMinor.password !== newMinor.confirmPassword) {
      setCreateError('Passwords do not match');
      return;
    }

    try {
      setCreating(true);
      setCreateError(null);
      
      let accountId = personalAccountId;
      
      if (!accountId) {
        const createResult = await jointAccountService.createAccount({
          accountType: 'FAMILY',
          accountName: `${user?.username || 'My'} Family`,
          description: 'Family account for minor access',
          isFamilyAccount: true,
          requireParentalApproval: true,
          defaultDailyLimit: 10,
          defaultPerTransactionLimit: 5,
        });
        if (createResult.success && createResult.jointAccountId) {
          accountId = createResult.jointAccountId;
          setPersonalAccountId(accountId);
        } else {
          setCreateError(createResult.message || 'Failed to create account');
          return;
        }
      }
      
      const result = await jointAccountService.inviteMember(accountId, {
        username: newMinor.username,
        role: 'MINOR',
        displayName: newMinor.displayName || newMinor.username,
        canSpend: true,
        canDeposit: false,
        canWithdraw: false,
        canInviteMembers: false,
        canEditSettings: false,
        requiresApproval: newMinor.requiresApproval,
        dailyLimit: newMinor.dailyLimit,
        weeklyLimit: newMinor.weeklyLimit,
        monthlyLimit: newMinor.monthlyLimit,
        perTransactionLimit: newMinor.perTransactionLimit,
        password: newMinor.password,
      });
      
      if (result.success) {
        setAddMinorDialogOpen(false);
        setNewMinor({
          username: '',
          displayName: '',
          password: '',
          confirmPassword: '',
          dailyLimit: 10,
          weeklyLimit: undefined,
          monthlyLimit: undefined,
          perTransactionLimit: 5,
          requiresApproval: true,
        });
        setShowNewPassword(false);
        loadAccountMembers();
      } else {
        setCreateError(result.message || 'Failed to add minor');
      }
    } catch (err: any) {
      setCreateError(err.response?.data?.message || 'Failed to add minor');
    } finally {
      setCreating(false);
    }
  };

  const handleEditMember = (member: JointAccountMember) => {
    const currentUserRole = members.find(m => m.username === user?.username)?.role;
    const isPrimaryUser = member.role === 'PRIMARY';
    const canEdit = currentUserRole === 'ADMIN' || (currentUserRole === 'PRIMARY' && !isPrimaryUser);
    
    if (isPrimaryUser && currentUserRole === 'PRIMARY') {
      setError('Primary owner cannot edit their own rights. Another admin must make changes.');
      return;
    }
    
    if (!canEdit) {
      setError('You do not have permission to edit this member');
      return;
    }
    
    setEditingMember(member);
    setEditPassword('');
    setEditConfirmPassword('');
    setShowEditPassword(false);
    // Initialise tab list from saved settings or sensible defaults
    if (member.role === 'MINOR') {
      const savedTabs = member.allowedTabs;
      setEditAllowedTabs(initialMinorEditAllowedTabs(savedTabs, configurableTabKeys));
    } else {
      setEditAllowedTabs([]);
    }
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingMember || !personalAccountId) return;

    if (editingMember.role === 'MINOR' && editPassword) {
      if (editPassword.length < 8) {
        setCreateError('Password must be at least 8 characters');
        return;
      }
      if (editPassword !== editConfirmPassword) {
        setCreateError('Passwords do not match');
        return;
      }
    }

    try {
      setCreating(true);
      setCreateError(null);
      
      await jointAccountService.updateMember(personalAccountId, editingMember.membershipId, {
        displayName: editingMember.displayName,
        dailyLimit: editingMember.dailyLimit,
        weeklyLimit: editingMember.weeklyLimit,
        monthlyLimit: editingMember.monthlyLimit,
        perTransactionLimit: editingMember.perTransactionLimit,
        requiresApproval: editingMember.requiresApproval,
        canSpend: editingMember.canSpend,
        newPassword: editingMember.role === 'MINOR' && editPassword ? editPassword : undefined,
        allowedTabs: editingMember.role === 'MINOR'
          ? editAllowedTabs.filter((key) => configurableTabKeys.includes(key))
          : undefined,
      });
      
      setEditDialogOpen(false);
      setEditingMember(null);
      setEditPassword('');
      setEditConfirmPassword('');
      setShowEditPassword(false);
      setEditAllowedTabs([]);
      loadAccountMembers();
    } catch (err: any) {
      setCreateError(err.response?.data?.message || 'Failed to update member');
    } finally {
      setCreating(false);
    }
  };

  const handleRemoveMember = async (membershipId: string, memberRole: string) => {
    if (!personalAccountId) return;
    
    if (memberRole === 'PRIMARY') {
      setError('Cannot remove primary owner. Transfer primary ownership first.');
      return;
    }
    
    if (!window.confirm('Are you sure you want to remove this member? This will revoke their access.')) return;

    try {
      await jointAccountService.removeMember(personalAccountId, membershipId);
      loadAccountMembers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove member');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const calculateAvailableCredits = (member: JointAccountMember): { available: number; limitType: string } | null => {
    const limits: { remaining: number; type: string }[] = [];
    if (member.dailyLimit !== undefined && member.dailyLimit > 0) {
      limits.push({ remaining: Math.max(0, member.dailyLimit - (member.currentDailySpent || 0)), type: 'Daily' });
    }
    if (member.weeklyLimit !== undefined && member.weeklyLimit > 0) {
      limits.push({ remaining: Math.max(0, member.weeklyLimit - (member.currentWeeklySpent || 0)), type: 'Weekly' });
    }
    if (member.monthlyLimit !== undefined && member.monthlyLimit > 0) {
      limits.push({ remaining: Math.max(0, member.monthlyLimit - (member.currentMonthlySpent || 0)), type: 'Monthly' });
    }
    if (limits.length === 0) return null;
    const mostRestrictive = limits.reduce((min, curr) => curr.remaining < min.remaining ? curr : min);
    return { available: mostRestrictive.remaining, limitType: mostRestrictive.type };
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'PRIMARY':
        return <Chip icon={<StarIcon />} label="Primary Owner" size="small" color="primary" />;
      case 'ADMIN':
        return <Chip label="Admin" size="small" color="secondary" />;
      case 'MINOR':
        return <Chip icon={<MinorIcon />} label="Minor" size="small" color="info" />;
      default:
        return <Chip label={role} size="small" />;
    }
  };

  const currentUserRole = members.find(m => m.username === user?.username)?.role;
  const canEditPrimary = currentUserRole === 'ADMIN' && currentUserRole !== 'PRIMARY';
  const primaryMember = members.find(m => m.role === 'PRIMARY');
  const minorMembers = members.filter(m => m.role === 'MINOR');
  const allMembersForDisplay = primaryMember 
    ? [primaryMember, ...minorMembers]
    : minorMembers;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <PersonIcon color="primary" />
            <Typography variant="h6">Minor Access Management</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Grant limited spending access to minors on your account. You can set spending limits 
            and require approval for their purchases.
          </Typography>
        </CardContent>
      </Card>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          <MinorIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Account Members ({allMembersForDisplay.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddMinorDialogOpen(true)}
        >
          Add Minor
        </Button>
      </Box>

      {allMembersForDisplay.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No members yet. Click "Add Minor" to give a child limited spending access.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Daily Limit</TableCell>
                <TableCell align="right">Available</TableCell>
                <TableCell>Approval</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allMembersForDisplay.map((member) => {
                const credits = calculateAvailableCredits(member);
                const isPrimary = member.role === 'PRIMARY';
                const canEdit = isPrimary ? canEditPrimary : (currentUserRole === 'PRIMARY' || currentUserRole === 'ADMIN');
                const canDelete = !isPrimary && (currentUserRole === 'PRIMARY' || currentUserRole === 'ADMIN');
                
                return (
                  <TableRow key={member.membershipId} sx={isPrimary ? { backgroundColor: 'action.hover' } : {}}>
                    <TableCell>
                      <Typography fontWeight="medium">
                        {member.displayName || member.username}
                      </Typography>
                    </TableCell>
                    <TableCell>{member.username}</TableCell>
                    <TableCell>{getRoleBadge(member.role)}</TableCell>
                    <TableCell align="right">
                      {isPrimary ? 'Unlimited' : (member.dailyLimit ? formatCurrency(member.dailyLimit) : '-')}
                    </TableCell>
                    <TableCell align="right">
                      {isPrimary ? '-' : (
                        credits ? (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 'bold', color: credits.available > 0 ? 'success.main' : 'error.main' }}
                            >
                              {formatCurrency(credits.available)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ({credits.limitType})
                            </Typography>
                          </Box>
                        ) : '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {isPrimary ? '-' : (
                        <Chip
                          label={member.requiresApproval ? 'Required' : 'Not Required'}
                          size="small"
                          color={member.requiresApproval ? 'warning' : 'default'}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {canEdit && (
                        <Tooltip title={isPrimary ? 'Edit Primary Rights' : 'Edit'}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditMember(member)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveMember(member.membershipId, member.role)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Minor Dialog */}
      <Dialog open={addMinorDialogOpen} onClose={() => setAddMinorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Minor</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}

          <Alert severity="info" sx={{ mb: 2 }}>
            Create a username for the minor. They will have limited spending access based on the limits you set.
          </Alert>

          <TextField
            fullWidth
            label="Minor's Username"
            value={newMinor.username}
            onChange={(e) => setNewMinor({ ...newMinor, username: e.target.value })}
            placeholder="e.g., Tommy"
            sx={{ mt: 2, mb: 2 }}
            helperText="This will be their username for accessing your account"
          />

          <TextField
            fullWidth
            label="Display Name (optional)"
            value={newMinor.displayName}
            onChange={(e) => setNewMinor({ ...newMinor, displayName: e.target.value })}
            placeholder="e.g., Tommy Jr."
            sx={{ mb: 2 }}
          />

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <LockIcon color="action" fontSize="small" />
            <Typography variant="subtitle2">Set Password for Minor</Typography>
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Create a password the minor will use to log in. They cannot change it themselves — only you can update it.
          </Alert>

          <TextField
            fullWidth
            label="Password"
            type={showNewPassword ? 'text' : 'password'}
            value={newMinor.password}
            onChange={(e) => setNewMinor({ ...newMinor, password: e.target.value })}
            sx={{ mb: 2 }}
            helperText="At least 8 characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowNewPassword(v => !v)}>
                    {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Confirm Password"
            type={showNewPassword ? 'text' : 'password'}
            value={newMinor.confirmPassword}
            onChange={(e) => setNewMinor({ ...newMinor, confirmPassword: e.target.value })}
            sx={{ mb: 2 }}
            error={!!newMinor.confirmPassword && newMinor.password !== newMinor.confirmPassword}
            helperText={newMinor.confirmPassword && newMinor.password !== newMinor.confirmPassword ? 'Passwords do not match' : ''}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Spending Limits
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Daily Limit"
                type="number"
                value={newMinor.dailyLimit || ''}
                onChange={(e) => setNewMinor({
                  ...newMinor,
                  dailyLimit: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Per Transaction Limit"
                type="number"
                value={newMinor.perTransactionLimit || ''}
                onChange={(e) => setNewMinor({
                  ...newMinor,
                  perTransactionLimit: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Weekly Limit (optional)"
                type="number"
                value={newMinor.weeklyLimit || ''}
                onChange={(e) => setNewMinor({
                  ...newMinor,
                  weeklyLimit: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Monthly Limit (optional)"
                type="number"
                value={newMinor.monthlyLimit || ''}
                onChange={(e) => setNewMinor({
                  ...newMinor,
                  monthlyLimit: e.target.value ? parseFloat(e.target.value) : undefined
                })}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Switch
                checked={newMinor.requiresApproval}
                onChange={(e) => setNewMinor({ ...newMinor, requiresApproval: e.target.checked })}
              />
            }
            label="Require my approval for all purchases"
          />
          {newMinor.requiresApproval && (
            <Alert severity="info" sx={{ mt: 1 }}>
              You will receive a message for each purchase request and must approve before the purchase goes through.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMinorDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddMinor} disabled={creating}>
            {creating ? <CircularProgress size={24} /> : 'Add Minor'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit {editingMember?.role === 'PRIMARY' ? 'Primary Owner Rights' : 'Minor Settings'}
        </DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}

          {editingMember && (
            <>
              <TextField
                fullWidth
                label="Display Name"
                value={editingMember.displayName || ''}
                onChange={(e) => setEditingMember({ ...editingMember, displayName: e.target.value })}
                sx={{ mt: 2, mb: 2 }}
              />

              {editingMember.role !== 'PRIMARY' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Spending Limits
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Daily Limit"
                        type="number"
                        value={editingMember.dailyLimit || ''}
                        onChange={(e) => setEditingMember({
                          ...editingMember,
                          dailyLimit: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Per Transaction"
                        type="number"
                        value={editingMember.perTransactionLimit || ''}
                        onChange={(e) => setEditingMember({
                          ...editingMember,
                          perTransactionLimit: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      />
                    </Grid>
                  </Grid>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={editingMember.requiresApproval || false}
                        onChange={(e) => setEditingMember({ ...editingMember, requiresApproval: e.target.checked })}
                      />
                    }
                    label="Requires Approval"
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={editingMember.canSpend || false}
                        onChange={(e) => setEditingMember({ ...editingMember, canSpend: e.target.checked })}
                      />
                    }
                    label="Can Spend"
                  />

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TabIcon color="action" fontSize="small" />
                    <Typography variant="subtitle2">Visible Tabs</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Choose which sections of the portal the minor can see. Only tabs available on your account are listed.
                  </Typography>
                  <FormGroup>
                    {configurableTabs.map(tab => (
                      <FormControlLabel
                        key={tab.key}
                        control={
                          <Checkbox
                            size="small"
                            checked={editAllowedTabs.includes(tab.key)}
                            onChange={(e) => {
                              setEditAllowedTabs(
                                e.target.checked
                                  ? [...editAllowedTabs, tab.key]
                                  : editAllowedTabs.filter(k => k !== tab.key)
                              );
                            }}
                          />
                        }
                        label={tab.label}
                      />
                    ))}
                  </FormGroup>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LockIcon color="action" fontSize="small" />
                    <Typography variant="subtitle2">Change Password</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Leave blank to keep the current password.
                  </Typography>
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showEditPassword ? 'text' : 'password'}
                    value={editPassword}
                    onChange={(e) => setEditPassword(e.target.value)}
                    sx={{ mb: 2 }}
                    helperText="At least 8 characters"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowEditPassword(v => !v)}>
                            {showEditPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type={showEditPassword ? 'text' : 'password'}
                    value={editConfirmPassword}
                    onChange={(e) => setEditConfirmPassword(e.target.value)}
                    error={!!editConfirmPassword && editPassword !== editConfirmPassword}
                    helperText={editConfirmPassword && editPassword !== editConfirmPassword ? 'Passwords do not match' : ''}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={creating}>
            {creating ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MinorAccessManager;
